import { useState, useEffect, useCallback } from 'react';
import { getAuditHistory } from '../api/commissions';
import type { CommissionAuditEntry } from '../types/commission';

/**
 * Hook for fetching commission audit history
 */
export function useAuditHistory(
  userId: number | null,
  startDate?: string,
  endDate?: string
) {
  const [data, setData] = useState<CommissionAuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditHistory = useCallback(async () => {
    if (!userId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const entries = await getAuditHistory(userId, startDate, endDate);
      setData(entries);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch audit history');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, startDate, endDate]);

  useEffect(() => {
    fetchAuditHistory();
  }, [fetchAuditHistory]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAuditHistory,
  };
}
